import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut, User, Menu, X } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    setMobileOpen(false);
    navigate("/");
  };

  const linkClass = ({ isActive }) =>
    `text-sm transition-colors hover:text-foreground ${
      isActive ? "text-foreground font-medium" : "text-muted-foreground"
    }`;

  const mobileLinkClass = ({ isActive }) =>
    `block rounded-lg px-4 py-3 text-base transition-colors ${
      isActive ? "bg-primary/10 font-medium text-forest" : "text-foreground hover:bg-muted"
    }`;

  const closeMobile = () => setMobileOpen(false);

  return (
    <header
      data-testid="main-navbar"
      className="sticky top-0 z-40 w-full border-b border-border/60 bg-background/85 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 lg:px-8">
        <Link
          to="/"
          data-testid="nav-logo-link"
          className="flex items-center gap-3"
        >
          <img
            src="/providence-partners-logo.png"
            alt="Providence Partners"
            className="h-12 w-12 rounded-full object-cover ring-1 ring-emerald/20 shadow-sm"
          />
          <div className="flex flex-col leading-tight">
            <span className="font-serif text-lg tracking-tight text-forest">
              Providence Partners
            </span>
            <span className="text-[11px] font-sans font-medium uppercase tracking-[0.14em] text-gold">
              Adult Day Care & Memory Care Locator
            </span>
          </div>
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          <NavLink to="/search" data-testid="nav-find-care" className={linkClass}>
            Find Care
          </NavLink>
          <NavLink to="/placement-help" data-testid="nav-placement-help" className={linkClass}>
            Senior Placement
          </NavLink>
          <NavLink to="/blog" data-testid="nav-blog" className={linkClass}>
            Care Tips
          </NavLink>
          <NavLink to="/for-facilities" data-testid="nav-for-facilities" className={linkClass}>
            For Facilities
          </NavLink>
          {user && user.role === "facility" && (
            <NavLink to="/dashboard/facility" data-testid="nav-facility-dashboard" className={linkClass}>
              My Facility
            </NavLink>
          )}
          {user && user.role === "caregiver" && (
            <NavLink to="/dashboard/caregiver" data-testid="nav-caregiver-dashboard" className={linkClass}>
              My Inquiries
            </NavLink>
          )}
          {user && user.role === "admin" && (
            <NavLink to="/dashboard/admin" data-testid="nav-admin-dashboard" className={linkClass}>
              Advisor Inbox
            </NavLink>
          )}
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <>
              <span className="hidden items-center gap-2 rounded-full bg-muted px-3 py-1.5 text-xs text-muted-foreground sm:flex">
                <User className="h-3.5 w-3.5" />
                <span data-testid="nav-user-name">{user.name}</span>
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                data-testid="nav-logout-btn"
                className="hidden rounded-full md:inline-flex"
              >
                <LogOut className="mr-1.5 h-4 w-4" /> Sign out
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                asChild
                className="hidden rounded-full md:inline-flex"
              >
                <Link to="/login" data-testid="nav-login-btn">
                  Sign in
                </Link>
              </Button>
              <Button
                size="sm"
                asChild
                className="hidden rounded-full bg-primary text-primary-foreground shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-transform md:inline-flex"
              >
                <Link to="/register" data-testid="nav-register-btn">
                  Get started
                </Link>
              </Button>
            </>
          )}
          <button
            type="button"
            onClick={() => setMobileOpen((o) => !o)}
            data-testid="nav-mobile-toggle"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border/60 bg-card text-forest hover:bg-muted md:hidden"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu panel */}
      {mobileOpen && (
        <div data-testid="nav-mobile-panel" className="border-t border-border/60 bg-background md:hidden">
          <nav className="mx-auto flex max-w-7xl flex-col gap-1 px-4 py-4">
            <NavLink to="/search" data-testid="m-nav-find-care" className={mobileLinkClass} onClick={closeMobile}>Find Care</NavLink>
            <NavLink to="/placement-help" data-testid="m-nav-placement-help" className={mobileLinkClass} onClick={closeMobile}>Senior Placement</NavLink>
            <NavLink to="/blog" data-testid="m-nav-blog" className={mobileLinkClass} onClick={closeMobile}>Care Tips</NavLink>
            <NavLink to="/for-facilities" data-testid="m-nav-for-facilities" className={mobileLinkClass} onClick={closeMobile}>For Facilities</NavLink>
            {user && user.role === "facility" && (
              <NavLink to="/dashboard/facility" data-testid="m-nav-facility-dashboard" className={mobileLinkClass} onClick={closeMobile}>My Facility</NavLink>
            )}
            {user && user.role === "caregiver" && (
              <NavLink to="/dashboard/caregiver" data-testid="m-nav-caregiver-dashboard" className={mobileLinkClass} onClick={closeMobile}>My Inquiries</NavLink>
            )}
            {user && user.role === "admin" && (
              <NavLink to="/dashboard/admin" data-testid="m-nav-admin-dashboard" className={mobileLinkClass} onClick={closeMobile}>Advisor Inbox</NavLink>
            )}
            <div className="mt-3 border-t border-border/60 pt-3">
              {user ? (
                <button onClick={handleLogout} data-testid="m-nav-logout" className="flex w-full items-center gap-2 rounded-lg px-4 py-3 text-base text-forest hover:bg-muted">
                  <LogOut className="h-4 w-4" /> Sign out ({user.name})
                </button>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <Link to="/login" onClick={closeMobile} data-testid="m-nav-login" className="rounded-lg border border-border/60 px-4 py-3 text-center text-base text-forest hover:bg-muted">Sign in</Link>
                  <Link to="/register" onClick={closeMobile} data-testid="m-nav-register" className="rounded-lg bg-primary px-4 py-3 text-center text-base text-primary-foreground">Get started</Link>
                </div>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
